import { defaultSchema } from 'rehype-sanitize';
import type { Schema } from 'hast-util-sanitize';

/**
 * Sanitize schema for markbird's rendered output.
 *
 * Security model: this schema is applied to the *untrusted* markdown-derived
 * tree BEFORE the trusted transform plugins (highlight, KaTeX, mermaid) run.
 * Those plugins therefore add their (trusted) output after sanitization, so we
 * only need to permit the structural class names and elements that our own
 * plugins depend on — never script, event handlers, or arbitrary styles on
 * user content.
 *
 * What we deliberately allow beyond the GitHub-ish default:
 *  - `className` on code/span/div/pre/section so `language-*`, `math*`,
 *    `markdown-alert*`, `footnotes`, and `mermaid` markers survive.
 *  - inline SVG octicons emitted by the GitHub-alert plugin.
 *  - task-list checkbox inputs (already in default, kept explicit).
 *
 * What stays denied: `script`, `style` elements, `on*` handlers, `javascript:`
 * URLs (default protocol allow-list is retained).
 */
export const sanitizeSchema: Schema = {
  ...defaultSchema,
  // remark-rehype already namespaces footnote ids/hrefs with `user-content-`.
  // Leave clobber-prefixing off here so sanitize doesn't double-prefix ids
  // (which would desync them from the un-rewritten href fragments and break
  // footnote navigation).
  clobberPrefix: '',
  attributes: {
    ...defaultSchema.attributes,
    '*': [...(defaultSchema.attributes?.['*'] ?? []), 'className', 'id', 'dataSourceLine'],
    code: [...(defaultSchema.attributes?.code ?? []), ['className', /^language-./, 'hljs']],
    span: [...(defaultSchema.attributes?.span ?? []), 'className'],
    div: [...(defaultSchema.attributes?.div ?? []), 'className'],
    pre: [...(defaultSchema.attributes?.pre ?? []), 'className'],
    section: [...(defaultSchema.attributes?.section ?? []), 'className'],
    // GitHub-alert octicons.
    svg: ['className', 'viewBox', 'width', 'height', 'ariaHidden', 'role', 'fill'],
    path: ['d', 'fillRule', 'clipRule'],
    // GFM task-list checkboxes.
    input: [...(defaultSchema.attributes?.input ?? []), 'type', 'checked', 'disabled'],
    // Footnote back-references and anchors.
    a: [
      ...(defaultSchema.attributes?.a ?? []),
      'ariaLabel',
      'dataFootnoteRef',
      'dataFootnoteBackref',
    ],
  },
  tagNames: [
    ...(defaultSchema.tagNames ?? []),
    'svg',
    'path',
    // KaTeX may emit MathML for accessibility; permit the container elements.
    'math',
    'semantics',
    'annotation',
    'mrow',
    'mi',
    'mo',
    'mn',
    'msup',
    'msub',
    'mfrac',
    'msqrt',
    'mroot',
    'mtable',
    'mtr',
    'mtd',
    'mspace',
    'mtext',
  ],
  // Retain default protocol allow-lists (blocks javascript:, data: on href/src)
  // but additionally permit markbird's own `mbimg://` scheme on image sources.
  // These refs point at device-local IndexedDB blobs; the app resolves them to
  // object/data URLs after sanitize. They carry no script/network risk — a ref
  // can at most display a local image the viewer already stored.
  protocols: {
    ...defaultSchema.protocols,
    src: [...(defaultSchema.protocols?.src ?? []), 'mbimg'],
  },
};
